import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { MaintenanceAPI } from "@/lib/api";
import { fromKm, toKm, unitLabel } from "@/lib/units";
import { CATEGORY_META } from "@/lib/maintenanceMeta";
import logger from "@/lib/logger";

/**
 * Add or edit a maintenance item (custom OR editing an auto-generated one).
 * Distance intervals convert via the app's unit toggle so users type in the
 * unit they see everywhere else.
 */
export default function MaintenanceItemDialog({
  open,
  onOpenChange,
  vehicles = [],
  defaultVehicleId,
  initialItem = null,
  unit = "km",
  onSaved,
}) {
  const isEdit = !!initialItem;
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialItem) {
      setForm({
        vehicle_id: initialItem.vehicle_id,
        name: initialItem.name || "",
        category: initialItem.category || "custom",
        interval_display: initialItem.interval_km != null
          ? String(Math.round(fromKm(initialItem.interval_km, unit)))
          : "",
        interval_months: initialItem.interval_months != null ? String(initialItem.interval_months) : "",
        last_service_date: initialItem.last_service_date || "",
        last_odo_display: initialItem.last_service_odometer_km != null
          ? String(Math.round(fromKm(initialItem.last_service_odometer_km, unit)))
          : "",
        notes: initialItem.notes || "",
      });
    } else {
      setForm({
        vehicle_id: defaultVehicleId || vehicles[0]?.id || "",
        name: "",
        category: "custom",
        interval_display: "",
        interval_months: "",
        last_service_date: "",
        last_odo_display: "",
        notes: "",
      });
    }
  }, [open, initialItem, defaultVehicleId, vehicles, unit]);

  const submit = async () => {
    if (!form) return;
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.vehicle_id) {
      toast.error("Pick a vehicle");
      return;
    }
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      category: form.category || "custom",
      interval_km: form.interval_display !== ""
        ? toKm(parseFloat(form.interval_display) || 0, unit)
        : null,
      interval_months: form.interval_months !== "" ? parseInt(form.interval_months, 10) : null,
      last_service_date: form.last_service_date || null,
      last_service_odometer_km: form.last_odo_display !== ""
        ? toKm(parseFloat(form.last_odo_display) || 0, unit)
        : null,
      notes: form.notes || "",
    };
    try {
      let saved;
      if (isEdit) {
        saved = await MaintenanceAPI.update(initialItem.id, payload);
      } else {
        saved = await MaintenanceAPI.create({
          vehicle_id: form.vehicle_id,
          ...payload,
        });
      }
      toast.success(isEdit ? "Item updated" : "Item added");
      onSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      logger.error("maintenance-item-save-failed", err?.message || String(err));
      toast.error("Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[var(--bg-elevated)] border-[var(--border-hover)] text-[var(--text-primary)] sm:max-w-[520px]"
        data-testid="maintenance-item-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg tracking-tight">
            {isEdit ? "Edit maintenance item" : "Add maintenance item"}
          </DialogTitle>
          <DialogDescription className="text-xs text-[var(--text-muted)]">
            Set a distance interval, a time interval, or both. The item is due when either trigger fires.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-1">
          {!isEdit && (
            <div className="space-y-1 col-span-2">
              <Label className="label-tiny">Vehicle</Label>
              <Select
                value={form.vehicle_id}
                onValueChange={(v) => setForm({ ...form, vehicle_id: v })}
              >
                <SelectTrigger
                  className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                  data-testid="maintenance-item-vehicle"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1 col-span-2 sm:col-span-1">
            <Label className="label-tiny">Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Brake pads"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
              data-testid="maintenance-item-name"
            />
          </div>
          <div className="space-y-1 col-span-2 sm:col-span-1">
            <Label className="label-tiny">Category</Label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm({ ...form, category: v })}
            >
              <SelectTrigger
                className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
                data-testid="maintenance-item-category"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]">
                {Object.entries(CATEGORY_META).map(([key, meta]) => (
                  <SelectItem key={key} value={key}>{meta.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Distance interval ({unitLabel(unit)})</Label>
            <Input
              type="number"
              min="0"
              value={form.interval_display}
              onChange={(e) => setForm({ ...form, interval_display: e.target.value })}
              placeholder="—"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-item-interval-km"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Time interval (months)</Label>
            <Input
              type="number"
              min="0"
              value={form.interval_months}
              onChange={(e) => setForm({ ...form, interval_months: e.target.value })}
              placeholder="—"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-item-interval-months"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Last serviced on</Label>
            <Input
              type="date"
              value={form.last_service_date}
              onChange={(e) => setForm({ ...form, last_service_date: e.target.value })}
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-item-last-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="label-tiny">Last odometer ({unitLabel(unit)})</Label>
            <Input
              type="number"
              min="0"
              value={form.last_odo_display}
              onChange={(e) => setForm({ ...form, last_odo_display: e.target.value })}
              placeholder="—"
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)] font-mono-data"
              data-testid="maintenance-item-last-odometer"
            />
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="label-tiny">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="bg-[var(--bg-canvas)] border-[var(--border-hover)] text-[var(--text-primary)]"
              data-testid="maintenance-item-notes"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="bg-transparent border-[var(--border-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"
            style={{ borderRadius: 2 }}
            data-testid="maintenance-item-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="flex-1 bg-[var(--bg-inverse)] text-[var(--text-inverse)] hover:bg-[var(--bg-inverse-hover)]"
            style={{ borderRadius: 2 }}
            data-testid="maintenance-item-save"
          >
            {submitting ? "Saving..." : isEdit ? "Save changes" : "Add item"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
